import { reset } from "./analytics";
import { apiUrl, AUTH_TOKEN_KEY } from "./api";
import type { User } from "@/store/authStore";
import type { AgentTask } from "@forgepoint/shared";
import {
  isEmailSendAction,
  type RichActionType,
} from "@/modules/agents/components/helpers/richActionHelpers";

interface TaskWithAssignees {
  assignees?: Array<{ assigneeUserId: string }>;
}

type AgentTaskWithOwners = Pick<AgentTask, "owners">;

/**
 * Check if a user can approve a task (can approve all tasks or assigned approver).
 * @param user - The authenticated user, or null when unauthenticated
 * @param task - The task with its assignees, if available
 * @param canApproveAllTasks - Whether the user holds the permission to approve any task
 * @returns True if the user can approve the task
 */
export const canUserApproveTask = (
  user: User | null,
  task: TaskWithAssignees | undefined,
  canApproveAllTasks: boolean,
): boolean => {
  if (!user) {
    return false;
  }
  const isAssignedApprover = task?.assignees?.some(
    (assignee) => assignee.assigneeUserId === user.id,
  );
  return canApproveAllTasks || !!isAssignedApprover;
};

/**
 * Check if a user can open a rich action's preview panel from the thread view.
 * Email sends are viewable by anyone; other actions require approval rights.
 */
export const canOpenRichActionPanel = (
  user: User | null,
  task: TaskWithAssignees | undefined,
  canApproveAllTasks: boolean,
  richActionType: RichActionType,
): boolean =>
  isEmailSendAction(richActionType) ||
  canUserApproveTask(user, task, canApproveAllTasks);

/**
 * Check if a user can approve an agent task (can approve all tasks or task owner).
 * @param user - The authenticated user, or null when unauthenticated
 * @param task - The agent task with its owners, if available
 * @param canApproveAllTasks - Whether the user holds the permission to approve any task
 * @returns True if the user can approve the agent task
 */
export const canUserApproveAgentTask = (
  user: User | null,
  task: AgentTaskWithOwners | null | undefined,
  canApproveAllTasks: boolean,
): boolean => {
  if (!user || !task) {
    return false;
  }
  if (canApproveAllTasks) {
    return true;
  }
  return task.owners.some(
    (owner) => owner.type === "userId" && owner.id === user.id,
  );
};

/**
 * Handles the logout process consistently across the application.
 * For WorkOS, we need to redirect to a server-side endpoint that will:
 * 1. Load the session from the token
 * 2. Get the WorkOS logout URL
 * 3. Redirect to the WorkOS logout URL
 *
 * @param logoutFunction - The logout function from the auth context that clears local state
 * @param onLogoutStart - Optional callback that runs when logout starts
 * @returns Promise that resolves when logout process starts
 */
export const handleLogout = async (): Promise<void> => {
  try {
    // Get the token before we clear it
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    reset();

    if (token) {
      window.location.href = `${apiUrl}/auth/logout?sessionToken=${encodeURIComponent(
        token,
      )}`;
      return;
    } else {
      window.location.href = `${apiUrl}/auth/logout`;
      return;
    }
  } catch (error) {
    window.location.href = `${apiUrl}/auth/logout`;
    throw error;
  }
};
